// Adapted from Vercel AI Elements (https://ai-sdk.dev/elements), Apache-2.0.
// Restyled for the XOBLACK design system (neutral scale, dark pairs) and with a
// local controllable-state hook instead of @radix-ui/react-use-controllable-state.
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { ChevronDownIcon, DotIcon, type LucideIcon } from 'lucide-react'
import {
  type ComponentProps,
  type ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

interface ChainContext {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const Ctx = createContext<ChainContext | null>(null)

const useChain = () => {
  const ctx = useContext(Ctx)
  if (!ctx)
    throw new Error('ChainOfThought parts must be inside ChainOfThought')
  return ctx
}

export type ChainOfThoughtProps = ComponentProps<'div'> & {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export const ChainOfThought = memo(
  ({
    className,
    open,
    defaultOpen = false,
    onOpenChange,
    children,
    ...props
  }: ChainOfThoughtProps) => {
    const [inner, setInner] = useState(defaultOpen)
    const isOpen = open ?? inner
    const setIsOpen = useCallback(
      (next: boolean) => {
        if (open === undefined) setInner(next)
        onOpenChange?.(next)
      },
      [open, onOpenChange]
    )
    const value = useMemo(() => ({ isOpen, setIsOpen }), [isOpen, setIsOpen])
    return (
      <Ctx.Provider value={value}>
        <div className={cn('space-y-3', className)} {...props}>
          {children}
        </div>
      </Ctx.Provider>
    )
  }
)

export type ChainOfThoughtHeaderProps = ComponentProps<
  typeof CollapsibleTrigger
> & { icon?: ReactNode }

export const ChainOfThoughtHeader = memo(
  ({ className, children, icon, ...props }: ChainOfThoughtHeaderProps) => {
    const { isOpen, setIsOpen } = useChain()
    return (
      <Collapsible onOpenChange={setIsOpen} open={isOpen}>
        <CollapsibleTrigger
          className={cn(
            'flex w-full items-center gap-2 text-left text-sm text-neutral-500 transition-colors hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white',
            className
          )}
          {...props}
        >
          {icon}
          <span className="min-w-0 flex-1">{children}</span>
          <ChevronDownIcon
            className={cn(
              'size-4 shrink-0 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </CollapsibleTrigger>
      </Collapsible>
    )
  }
)

export type ChainOfThoughtStepProps = ComponentProps<'div'> & {
  icon?: LucideIcon
  /** A custom mark (e.g. an app logo) shown instead of the icon. */
  iconNode?: ReactNode
  label: ReactNode
  description?: ReactNode
  status?: 'complete' | 'active' | 'pending' | 'error'
  last?: boolean
}

export const ChainOfThoughtStep = memo(
  ({
    className,
    icon: Icon = DotIcon,
    iconNode,
    label,
    description,
    status = 'complete',
    last,
    children,
    ...props
  }: ChainOfThoughtStepProps) => (
    <div
      className={cn(
        'flex gap-3 text-sm animate-in fade-in-0 slide-in-from-top-2',
        status === 'active'
          ? 'text-neutral-900 dark:text-white'
          : 'text-neutral-600 dark:text-neutral-300',
        status === 'pending' && 'opacity-50',
        className
      )}
      {...props}
    >
      <div className="relative flex flex-col items-center">
        <span
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full',
            status === 'error'
              ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
              : status === 'active'
                ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
          )}
        >
          {iconNode ?? <Icon className="size-3.5" />}
        </span>
        {!last && (
          <span className="mt-1 w-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1 pb-3">
        <div className="leading-6">{label}</div>
        {description && (
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            {description}
          </div>
        )}
        {children}
      </div>
    </div>
  )
)

export type ChainOfThoughtContentProps = ComponentProps<
  typeof CollapsibleContent
>

export const ChainOfThoughtContent = memo(
  ({ className, children, ...props }: ChainOfThoughtContentProps) => {
    const { isOpen } = useChain()
    return (
      <Collapsible open={isOpen}>
        <CollapsibleContent
          className={cn(
            'outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:animate-in data-[state=open]:slide-in-from-top-2',
            className
          )}
          {...props}
        >
          {children}
        </CollapsibleContent>
      </Collapsible>
    )
  }
)

ChainOfThought.displayName = 'ChainOfThought'
ChainOfThoughtHeader.displayName = 'ChainOfThoughtHeader'
ChainOfThoughtStep.displayName = 'ChainOfThoughtStep'
ChainOfThoughtContent.displayName = 'ChainOfThoughtContent'
